import { API } from 'api';
import { AxiosResponse, HttpStatusCode, isAxiosError } from 'axios';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, UseQueryResult } from '@tanstack/react-query';
import * as Yup from 'yup';
import { attemptAsync } from 'es-toolkit/util';
import { getErrorsListFromServerResponse } from 'shared/utils';
import { safeParseDate } from 'shared/dates';
import { getCentroid } from 'features/mapping/mapping.utils';
import {
  FirePerimeter,
  FirePerimeterApprovalStatus,
  FirePerimeterFormValues,
  FirePerimeterGeoEvent,
  FirePerimeterPatch,
  FirePerimeterServer,
  FirePerimeterType,
} from './FirePerimeterForm.types';

const PERIMETER_PREVIEW_COLORS = {
  pending: '#FF7900',
  approvedActive: '#FF0000',
  approvedHistorical: '#CFCFCF',
} as const;

/**
 * The type the perimeter will save as. The server's `type` covers the incident linked
 * at load time, so once the reviewer picks a different one we read that option's own
 * `isActive` instead.
 */
export const getEffectivePerimeterType = (
  selectedGeoEvent: FirePerimeterGeoEvent | null,
  firePerimeter: FirePerimeter,
): FirePerimeterType | null => {
  if (!selectedGeoEvent) return null;
  if (selectedGeoEvent.id === firePerimeter.geoEventId) {
    return firePerimeter.type;
  }
  if (selectedGeoEvent.isActive == null) return null;
  return selectedGeoEvent.isActive ? 'active' : 'historical';
};

/**
 * Fill color for the perimeter preview: red approving an active fire, grey historical,
 * orange otherwise. `approvalStatus` is the pending form selection, so orange covers
 * both "not approved yet" and "no type to derive".
 */
export const getPerimeterPreviewColor = (
  approvalStatus: FirePerimeterApprovalStatus,
  type: FirePerimeterType | null,
): string => {
  if (approvalStatus === 'approved') {
    if (type === 'active') return PERIMETER_PREVIEW_COLORS.approvedActive;
    if (type === 'historical')
      return PERIMETER_PREVIEW_COLORS.approvedHistorical;
  }
  return PERIMETER_PREVIEW_COLORS.pending;
};

export const approvalStatusOptions: {
  value: FirePerimeterApprovalStatus;
  transKey: string;
}[] = [
  { value: 'pending', transKey: 'editFirePerimeter.approvalStatus.pending' },
  { value: 'approved', transKey: 'editFirePerimeter.approvalStatus.approved' },
  { value: 'rejected', transKey: 'editFirePerimeter.approvalStatus.rejected' },
];

const parseFirePerimeter = (
  firePerimeter: FirePerimeterServer,
): FirePerimeter => ({
  ...firePerimeter,
  sourceDateCurrent: safeParseDate(firePerimeter.sourceDateCurrent) ?? null,
  center: firePerimeter.geom ? getCentroid(firePerimeter.geom) : null,
  dateCreated: new Date(firePerimeter.dateCreated),
  dateModified: new Date(firePerimeter.dateModified),
});

const fetchFirePerimeter = async (id: number): Promise<FirePerimeter> => {
  const response = await API.get(`fire_perimeters/${id}`);
  return parseFirePerimeter(response.data);
};

export const updateFirePerimeter = (input: {
  id: number;
  data: FirePerimeterPatch;
}): Promise<AxiosResponse<FirePerimeterServer>> =>
  API.patch(`fire_perimeters/${input.id}`, input.data);

export type UseFirePerimeterResult = UseQueryResult<FirePerimeter> & {
  updateFirePerimeter: (
    patch: FirePerimeterPatch,
  ) => Promise<{ errorMessage: string | null }>;
};

export const useFirePerimeter = (id?: number): UseFirePerimeterResult => {
  const { t } = useTranslation();

  const query = useQuery({
    queryKey: ['firePerimeter', id],
    queryFn: () => fetchFirePerimeter(id!),
    enabled: Boolean(id),
  });

  const mutation = useMutation({ mutationFn: updateFirePerimeter });

  const update = async (
    patch: FirePerimeterPatch,
  ): Promise<{ errorMessage: string | null }> => {
    const [error] = await attemptAsync(() =>
      mutation.mutateAsync({ id: id!, data: patch }),
    );

    if (!error) return { errorMessage: null };

    if (!isAxiosError(error)) {
      return {
        errorMessage:
          error instanceof Error ? error.message : t('common.unknownError'),
      };
    }

    const errorStatus = error.response?.status;

    if (errorStatus === HttpStatusCode.Forbidden) {
      return { errorMessage: t('editFirePerimeter.errors.forbidden') };
    }

    if (errorStatus === HttpStatusCode.BadRequest) {
      const errors = getErrorsListFromServerResponse(
        error.response?.data ?? {},
      );
      return {
        errorMessage:
          errors.length > 0
            ? errors.join(' ')
            : t('editFirePerimeter.errors.badRequest'),
      };
    }

    return {
      errorMessage:
        error instanceof Error ? error.message : t('common.unknownError'),
    };
  };

  return { ...query, updateFirePerimeter: update };
};

export const getInitialValues = (
  firePerimeter: FirePerimeter,
): FirePerimeterFormValues => ({
  approvalStatus: firePerimeter.approvalStatus,
  geoEvent:
    firePerimeter.geoEventId != null
      ? { id: firePerimeter.geoEventId, name: firePerimeter.geoEventName ?? '' }
      : null,
});

export const formValuesToPatch = (
  values: FirePerimeterFormValues,
  firePerimeter: FirePerimeter,
): FirePerimeterPatch => {
  const patch: FirePerimeterPatch = {};

  if (values.approvalStatus !== firePerimeter.approvalStatus) {
    patch.approvalStatus = values.approvalStatus;
  }

  const nextGeoEventId = values.geoEvent?.id ?? null;
  if (nextGeoEventId !== firePerimeter.geoEventId) {
    patch.geoEventId = nextGeoEventId;
  }

  return patch;
};

// Approving requires a linked incident, which also decides active vs historical.
export const getValidationSchema = (t: TFunction): Yup.SchemaOf<object> =>
  Yup.object().shape({
    approvalStatus: Yup.string().required(
      t('editFirePerimeter.inputs.approvalStatus.required'),
    ),
    geoEvent: Yup.object()
      .nullable()
      .when('approvalStatus', {
        is: 'approved',
        then: Yup.object()
          .nullable()
          .required(t('editFirePerimeter.inputs.incident.required')),
      }),
  });
